import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import FormField from '../../../widgets/FormField';
import SchemaSelectorModal from './SchemaSelectorModal';
import api from '../../../API';
import { readNested, assignNested } from '../../../helpers/assignfield';
import { IDENTITY_DETAIL_KEYS } from './shared/utils/clinicalFieldSummary';
import styles from '../../../widgets/CrudPanel.module.css';

/**
 * Dedicated clinical metadata form (no CrudPanel).
 * Schema selector + schema-derived detail fields only; single Save → PUT bdsa/case.
 * Renders inside a panel wrapper in CaseBuilder; when !item the panel is disabled by parent.
 */
const CaseBuilderPanel = ({ item, onSave, schemas, protocolId }) => {
    const queryClient = useQueryClient();
    const [appliedSchema, setAppliedSchema] = useState(null);
    const [showSchemaModal, setShowSchemaModal] = useState(false);
    const [formData, setFormData] = useState({});
    // Latest form data for handleSave; keep in sync wherever formData is set.
    const formDataRef = useRef({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [savePending, setSavePending] = useState(false);
    const [saveResponse, setSaveResponse] = useState(undefined);

    useEffect(() => {
        if (!item?.schemaId && schemas?.length > 0 && !appliedSchema) {
            const mostRecent = schemas[schemas.length - 1];
            setAppliedSchema(mostRecent);
        }
    }, [item, schemas, appliedSchema]);

    useEffect(() => {
        if (item?.schemaId && schemas?.length > 0 && !appliedSchema) {
            const match = schemas.find((s) => s._id === item.schemaId);
            if (match) setAppliedSchema(match);
        }
    }, [item, schemas, appliedSchema]);

    const currentSchema = appliedSchema ?? (item?.schema ? { definition: item.schema } : null);
    const schemaProperties = currentSchema?.definition?.properties ?? {};
    const hasSchema = !!currentSchema?.definition?.properties;
    const detailFields = hasSchema
        ? Object.entries(schemaProperties)
              .filter(([key]) => !IDENTITY_DETAIL_KEYS.has(key))
              .map(([key, val]) => ({
                  ...val,
                  key: `details.${key}`,
                  body: `details.${key}`,
                  type: val.type === 'number' ? 'integer' : val.type,
              }))
        : [];

    useEffect(() => {
        if (item && detailFields.length > 0) {
            const initial = {};
            detailFields.forEach((f) => {
                if (f.key) {
                    const v = readNested(item, f.key);
                    initial[f.key] = v !== undefined ? v : f.defaultValue;
                }
            });
            setFormData(initial);
            formDataRef.current = initial;
            setErrors({});
            setTouched({});
        } else {
            setFormData({});
            formDataRef.current = {};
        }
    // Re-hydrate when case identity, schema, or details change (e.g. NACC clinical import).
    }, [item?._id, appliedSchema?._id, item?.updatedAt, item?.details]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFieldChange = (fieldKey, value) => {
        setFormData((prev) => {
            const next = { ...prev, [fieldKey]: value };
            formDataRef.current = next;
            return next;
        });
        if (errors[fieldKey]) setErrors((prev) => ({ ...prev, [fieldKey]: undefined }));
    };

    const handleFieldBlur = (fieldKey) => {
        setTouched((prev) => ({ ...prev, [fieldKey]: true }));
    };

    const validate = () => {
        const snapshot = formDataRef.current;
        const nextErrors = {};
        detailFields.forEach((f) => {
            if (f.required) {
                const v = snapshot[f.key];
                if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
                    nextErrors[f.key] = 'This field is required';
                }
            }
        });
        setErrors(nextErrors);
        setTouched(Object.fromEntries(detailFields.filter((f) => f.key).map((f) => [f.key, true])));
        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = (e) => {
        e.preventDefault();
        const schemaId = appliedSchema?._id ?? item.schemaId;
        if (!item?._id || !schemaId || !validate()) return;
        setSavePending(true);
        setSaveResponse(undefined);
        // Preserve identity aliases (e.g. otherCaseIdentifiers / NACCID) and any other
        // non-form detail keys when saving clinical fields.
        const body = {
            schemaId,
            details: { ...(item.details || {}) },
            protocol_id: protocolId ?? item?.protocolId ?? null,
            quarantined: item.quarantined,
        };
        const snapshot = formDataRef.current;
        detailFields.forEach((f) => {
            if (f.key && f.body) {
                const value = snapshot[f.key];
                assignNested(body, f.body, value);
            }
        });
        api.put(`/bdsa/case/${item._id}`, { body })
            .then((response) => {
                setSavePending(false);
                setSaveResponse(response.status === 200);
                if (response.status === 200 && response.data) {
                    onSave(response.data);
                    queryClient.invalidateQueries({ queryKey: ['bdsa-case-list'] });
                    if (item.networkIdentifier) {
                        queryClient.invalidateQueries({ queryKey: ['bdsa-case', item.networkIdentifier] });
                    }
                }
            })
            .catch(() => {
                setSavePending(false);
                setSaveResponse(false);
            });
    };

    if (!item) {
        return null;
    }

    return (
        <>
            {schemas?.length > 0 && (
                <div style={{ marginBottom: '0.5em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                    <span style={{ fontSize: '0.9em', color: '#666' }}>
                        Schema: {appliedSchema?.version != null ? `v${appliedSchema.version}` : 'Not set'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowSchemaModal(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#007bff',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '0.9em',
                            padding: 0,
                        }}
                    >
                        {hasSchema ? 'Change Schema Version' : 'Select Schema Version'}
                    </button>
                </div>
            )}
            {hasSchema && (
                <form onSubmit={handleSave} className={styles.form}>
                    <div className={styles.formFields}>
                        {detailFields.map((f, i) =>
                            f.key ? (
                                <FormField
                                    key={`${f.key}-${i}`}
                                    field={f}
                                    value={formData[f.key]}
                                    onChange={(value) => handleFieldChange(f.key, value)}
                                    onBlur={() => handleFieldBlur(f.key)}
                                    error={touched[f.key] ? errors[f.key] : undefined}
                                    isChanged={false}
                                />
                            ) : null
                        )}
                    </div>
                    <div className={styles.formActions}>
                        <button
                            type="submit"
                            className={styles.buttonPrimary}
                            disabled={savePending || !(appliedSchema?._id ?? item?.schemaId)}
                        >
                            {savePending ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                    {typeof saveResponse === 'boolean' && (
                        <div className={saveResponse ? styles.successMessage : styles.errorMessage}>
                            {saveResponse ? 'Save was successful' : 'Save failed'}
                        </div>
                    )}
                </form>
            )}
            <SchemaSelectorModal
                isOpen={showSchemaModal}
                onClose={() => setShowSchemaModal(false)}
                schemas={schemas}
                currentSchemaId={appliedSchema?._id ?? item?.schemaId}
                onApply={setAppliedSchema}
            />
        </>
    );
};

export default CaseBuilderPanel;
